export interface Question {
  id: string;
  left: string;
  right: string;
  leftVal: number;
  rightVal: number;
  points: number;
}

export function generateQuestion(QuestionNumber: number): Question {
  const id = Math.random().toString(36).substr(2, 9);
  const getRandomInt = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

  let leftVal: number, rightVal: number;
  let leftStr: string, rightStr: string;
  let points = 10;

  // Question reset to 1 on mistake handled in Game.tsx
  
  do {
    if (QuestionNumber <= 15) {
      // Phase 1: Warm Up (Q1-15) - 1 to 9 comparison
      leftVal = getRandomInt(1, 9);
      rightVal = getRandomInt(1, 9);
      leftStr = leftVal.toString();
      rightStr = rightVal.toString();
      points = 20;
    } else if (QuestionNumber <= 25) {
      // Phase 2: Sums (Q16-25) - 1..9 + 1..9 vs 1..9
      const side = Math.random() > 0.5 ? 'left' : 'right';
      const a = getRandomInt(1, 9);
      const b = getRandomInt(1, 9);
      const eqVal = a + b;
      const numVal = getRandomInt(1, 9); // Capped at 9
      
      leftVal = side === 'left' ? eqVal : numVal;
      leftStr = side === 'left' ? `${a} + ${b}` : numVal.toString();
      rightVal = side === 'right' ? eqVal : numVal;
      rightStr = side === 'right' ? `${a} + ${b}` : numVal.toString();
      points = 50;
    } else if (QuestionNumber <= 35) {
      // Phase 3: Diffs (Q26-35) - 1..9 - 1..9 vs 1..9
      const side = Math.random() > 0.5 ? 'left' : 'right';
      let a = getRandomInt(1, 9);
      let b = getRandomInt(1, 9);
      if (a < b) [a, b] = [b, a]; // Ensure positive result
      const eqVal = a - b;
      const numVal = getRandomInt(1, 9); // Capped at 9
      
      leftVal = side === 'left' ? eqVal : numVal;
      leftStr = side === 'left' ? `${a} - ${b}` : numVal.toString();
      rightVal = side === 'right' ? eqVal : numVal;
      rightStr = side === 'right' ? `${a} - ${b}` : numVal.toString();
      points = 100;
    } else if (QuestionNumber <= 55) {
      // Phase 4: Mixed (Q36-55) - both sides do not need to be equations
      const generateSide = (type: 'multi' | 'sum' | 'number') => {
        if (type === 'multi') {
          const a = getRandomInt(2, 9);
          const b = getRandomInt(2, 9);
          return { val: a * b, str: `${a} × ${b}` };
        } else if (type === 'sum') {
          const a = getRandomInt(1, 9);
          const b = getRandomInt(1, 9);
          return { val: a + b, str: `${a} + ${b}` };
        } else {
          const val = getRandomInt(2, 15);
          return { val, str: val.toString() };
        }
      };

      let leftType = (['multi', 'sum', 'number'] as const)[getRandomInt(0, 2)];
      let rightType = (['multi', 'sum', 'number'] as const)[getRandomInt(0, 2)];
      if (leftType === 'number' && rightType === 'number') {
        // Force at least one side to be an equation
        if (Math.random() > 0.5) {
          leftType = Math.random() > 0.5 ? 'multi' : 'sum';
        } else {
          rightType = Math.random() > 0.5 ? 'multi' : 'sum';
        }
      }

      const l = generateSide(leftType);
      const r = generateSide(rightType);
      leftVal = l.val;
      leftStr = l.str;
      rightVal = r.val;
      rightStr = r.str;
      points = 200;
    } else {
      // Phase 5: The Gauntlet (Q56+) - both sides do not need to be equations, diff < 5
      const generateSide = (type: 'multi' | 'sum' | 'diff' | 'number') => {
        if (type === 'multi') {
          const a = getRandomInt(2, 9);
          const b = getRandomInt(2, 9);
          return { val: a * b, str: `${a} × ${b}` };
        } else if (type === 'sum') {
          const a = getRandomInt(1, 9);
          const b = getRandomInt(1, 9);
          return { val: a + b, str: `${a} + ${b}` };
        } else if (type === 'diff') {
          let a = getRandomInt(1, 9);
          let b = getRandomInt(1, 9);
          if (a < b) [a, b] = [b, a]; // Ensure positive result
          return { val: a - b, str: `${a} - ${b}` };
        } else {
          const val = getRandomInt(2, 15);
          return { val, str: val.toString() };
        }
      };

      const getGauntletPair = () => {
        let leftType = (['multi', 'sum', 'diff', 'number'] as const)[getRandomInt(0, 3)];
        let rightType = (['multi', 'sum', 'diff', 'number'] as const)[getRandomInt(0, 3)];
        if (leftType === 'number' && rightType === 'number') {
          if (Math.random() > 0.5) {
            leftType = (['multi', 'sum', 'diff'] as const)[getRandomInt(0, 2)];
          } else {
            rightType = (['multi', 'sum', 'diff'] as const)[getRandomInt(0, 2)];
          }
        }
        return {
          l: generateSide(leftType),
          r: generateSide(rightType)
        };
      };

      let pair = getGauntletPair();
      while (Math.abs(pair.l.val - pair.r.val) >= 5 || pair.l.val === pair.r.val) {
        pair = getGauntletPair();
      }

      leftVal = pair.l.val;
      leftStr = pair.l.str;
      rightVal = pair.r.val;
      rightStr = pair.r.str;
      points = 500;
    }
  } while (leftVal === rightVal);




  return { id, left: leftStr, right: rightStr, leftVal, rightVal, points };
}

export function calculateExp(stats: {
  score: number;
  correctAnswers: number;
  feverAnswers: number;
  mistakes: number;
  isWin?: boolean;
}): number {
  // Exp = score + 50 base exp
  return stats.score + 50;
}


export function getExpToNextLevel(level: number): number {
  if (level >= 100) return Infinity; // Hard cap level 100
  return 100 * Math.pow(level, 2);
}
